import React, { useState, useCallback, useMemo } from "react"
import { useForm, Controller } from "react-hook-form"
import { Box, Button, Divider, Grid, IconButton, styled, Typography } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { ControlPoint, RemoveCircle } from "@mui/icons-material"
import moment from "moment"

// API and type imports
import { invoiceStatusOptions } from "src/api/modules/invoice-api"
import {
  ContainerInvoiceStatus,
  GetPaymentContainersParams,
  PaymentContainer,
  useLazyGetPaymentContainersQuery
} from "src/api/modules/container-api"

// Component imports
import OutlinedInput from "src/shared/components/outlined-input"
import InputAutocomplete from "src/shared/components/input-autocomplete"
import DatePicker from "src/shared/components/date-picker"
import { PageLoader, Stack } from "src/shared/components"

// Local imports
import SyncPaymentContainerModal, { SyncPaymentContainerFormParams } from "./sync-payment-container-modal"
import CreateInvoiceModal from "./create-invoice-modal"
import { getPaymentContainerColumns } from "./getPaymentContainerColumns"
import SelectVendorField from "src/shared/container/select-vendor-field"
import { toast } from "react-toastify"
import { useNavigate } from "react-router-dom"
import ROUTE_PATHS from "src/routes/route-path"

/**
 * Form data structure for creating an invoice
 */
export type CreateInvoiceForm = {
  invoiceNumber?: string
  invoiceDate?: string
  dueDate?: string
  clientName?: string
  taxRate?: number
  status?: string
  paymentContainerParams?: {
    port?: IOptions | null
    containerType?: IOptions | null
    vendor?: IOptions | null
    description?: IOptions | null
    invoiceStatus?: ContainerInvoiceStatus
  }
}

/**
 * Pagination configuration for payment containers
 */

/**
 * DataGrid styling configuration
 */
const DATA_GRID_STYLES = {
  height: 400,
  border: "1px solid #e0e0e0",
  borderRadius: "8px"
} as const

const DEFAULT_VALUES = {
  invoiceNumber: "",
  invoiceDate: moment().toISOString(),
  dueDate: moment().toISOString(),
  clientName: "",
  taxRate: 0,
  status: "",
  paymentContainerParams: {
    port: null,
    containerType: null,
    vendor: null,
    description: null,
    invoiceStatus: ContainerInvoiceStatus.DRAFT
  }
}
/**
 * CreateInvoice Component
 *
 * This component allows users to create invoices by:
 * 1. Filling out basic invoice information (client, dates, tax rate, status)
 * 2. Syncing payment containers based on selected criteria
 * 3. Selecting/deselecting payment containers for the invoice
 * 4. Creating the final invoice with selected containers
 */
const PAGE_SIZE_OPTIONS = [10, 20, 50]
const CreateInvoice: React.FC = () => {
  // ===== STATE MANAGEMENT =====
  const navigate = useNavigate()
  // Pagination state for payment containers
  const [paymentContainerPageNo, setPaymentContainerPageNo] = useState<number>(1) // zero-based for DataGrid
  const [paymentContainerPageSize, setPaymentContainerPageSize] = useState<number>(10)
  const [totalPaymentContainers, setTotalPaymentContainers] = useState(0)

  // Payment containers data state
  const [paymentContainers, setPaymentContainers] = useState<PaymentContainer[]>([])
  const [selectedPaymentContainers, setSelectedPaymentContainers] = useState<PaymentContainer[]>([])

  // Modal visibility state
  const [isSyncPaymentContainerModalOpen, setIsSyncPaymentContainerModalOpen] = useState(false)
  const [isCreateInvoiceModalOpen, setIsCreateInvoiceModalOpen] = useState(false)

  // Invoice data state for modal
  const [invoiceData, setInvoiceData] = useState<CreateInvoiceForm>()

  // ===== HOOKS =====

  // Form management with react-hook-form
  const { control, handleSubmit, getValues, watch, setValue, reset } = useForm<CreateInvoiceForm>({
    defaultValues: DEFAULT_VALUES
  })

  // Watch payment container parameters for real-time updates
  const paymentContainerParams = watch("paymentContainerParams")

  // API query hook for fetching payment containers
  const [getPaymentContainers, { isLoading: isLoadingPaymentContainers, isFetching: isFetchingPaymentContainers }] =
    useLazyGetPaymentContainersQuery()

  // ===== COMPUTED VALUES =====

  // Determine if the page is in a loading state
  const isPageLoading = useMemo(
    () => isLoadingPaymentContainers || isFetchingPaymentContainers,
    [isLoadingPaymentContainers, isFetchingPaymentContainers]
  )

  // ===== EVENT HANDLERS =====

  /**
   * Handles form reset and cleanup
   */
  const handleClose = useCallback(() => {
    reset(DEFAULT_VALUES)
  }, [reset])

  /**
   * Handles invoice form submission
   * Opens the create invoice modal with form data
   */
  const onSubmitInvoiceForm = useCallback((_data: CreateInvoiceForm) => {
    setInvoiceData(_data)
    setIsCreateInvoiceModalOpen(true)
  }, [])

  /**
   * Fetches payment containers based on provided parameters
   * Updates the containers list and resets selection
   */
  const fetchPaymentContainers = useCallback(
    async (data: GetPaymentContainersParams) => {
      try {
        console.log("paymentContainerPageNo", paymentContainerPageNo)
        const resp = await getPaymentContainers({
          page: paymentContainerPageNo,
          limit: paymentContainerPageSize,
          ...data
        })

        if (resp.data?.data) {
          setPaymentContainers(resp.data.data)
          setSelectedPaymentContainers([]) // Reset selection when new data is fetched
          setTotalPaymentContainers(resp.data.meta?.total ?? 0)
        }
      } catch (error) {
        setPaymentContainers([])
        setSelectedPaymentContainers([])
        setTotalPaymentContainers(0)
        toast.error("Error fetching payment containers")
        console.error("Error fetching payment containers:", error)
        // TODO: Add error handling/toast notification
      }
    },
    [getPaymentContainers, paymentContainerPageNo, paymentContainerPageSize]
  )

  /**
   * Handles payment container form submission
   * Updates form values and fetches containers
   */
  const onSubmitPaymentContainerForm = useCallback(
    (data: SyncPaymentContainerFormParams) => {
      console.log("data-onSubmitPaymentContainerForm", data)
      setValue("paymentContainerParams", data)
      fetchPaymentContainers({
        portId: data.port?.value,
        containerTypeId: data.containerType?.value,
        vendorId: data.vendor?.value,
        descriptionId: data.description?.value,
        invoiceStatus: data.invoiceStatus
      })
      setIsSyncPaymentContainerModalOpen(false)
    },
    [setValue, fetchPaymentContainers]
  )

  /**
   * Handles selecting a payment container
   * Moves container from available to selected list
   */
  const handleSelectPaymentContainer = useCallback((container: PaymentContainer) => {
    setSelectedPaymentContainers(prev => [...prev, container])
    setPaymentContainers(prev => prev.filter(item => item.id !== container.id))
  }, [])

  /**
   * Handles unselecting a payment container
   * Moves container from selected back to available list
   */
  const handleUnselectPaymentContainer = useCallback((container: PaymentContainer) => {
    setSelectedPaymentContainers(prev => prev.filter(item => item.id !== container.id))
    setPaymentContainers(prev => [...prev, container])
  }, [])

  /**
   * Handles pagination changes for payment containers
   */
  const handlePaginationChange = useCallback(
    (page: number, pageSize: number) => {
      const params = getValues("paymentContainerParams")
      setPaymentContainerPageNo(page)
      setPaymentContainerPageSize(pageSize)
      fetchPaymentContainers({
        portId: params?.port?.value,
        containerTypeId: params?.containerType?.value,
        vendorId: params?.vendor?.value,
        descriptionId: params?.description?.value,
        invoiceStatus: params?.invoiceStatus
      })
    },
    [fetchPaymentContainers, getValues]
  )

  /**
   * Handles modal toggle for sync payment container modal
   */
  const toggleSyncModal = useCallback(() => {
    setIsSyncPaymentContainerModalOpen(prev => !prev)
  }, [])

  /**
   * Handles modal toggle for create invoice modal
   */
  const toggleCreateInvoiceModal = useCallback(() => {
    setIsCreateInvoiceModalOpen(prev => !prev)
  }, [])

  /**
   * Handles cleanup after invoice creation
   * Resets all state to initial values
   */
  const handleAfterInvoiceSubmit = useCallback(() => {
    navigate(ROUTE_PATHS.INVOICE)
    setInvoiceData(undefined)
    setSelectedPaymentContainers([])
    reset(DEFAULT_VALUES)
    setPaymentContainers([])
    setPaymentContainerPageNo(1)
    setPaymentContainerPageSize(10)
    // setIsCreateInvoiceModalOpen(false)
  }, [reset])

  // ===== MEMOIZED COMPONENTS =====

  /**
   * Renders the action cell for available payment containers
   */
  const availableContainersActionCell = useMemo(
    () => (params: any) => (
      <IconButton aria-label="add-container" onClick={() => handleSelectPaymentContainer(params.row)}>
        <ControlPoint />
      </IconButton>
    ),
    [handleSelectPaymentContainer]
  )

  /**
   * Renders the action cell for selected payment containers
   */
  const selectedContainersActionCell = useMemo(
    () => (params: any) => (
      <IconButton aria-label="remove-container" onClick={() => handleUnselectPaymentContainer(params.row)}>
        <RemoveCircle color="error" />
      </IconButton>
    ),
    [handleUnselectPaymentContainer]
  )

  /**
   * Renders the payment container parameters display
   */
  const renderPaymentContainerParams = useMemo(
    () => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 1,
          mt: 2
        }}
      >
        <Box sx={{ minWidth: "200px", flex: "1 1 auto", textAlign: "center" }}>
          <Typography variant="subtitle1" textAlign="center">
            <strong>Port: </strong> {paymentContainerParams?.port?.label || "Not selected"}
          </Typography>
        </Box>
        <Box sx={{ minWidth: "200px", flex: "1 1 auto", textAlign: "center" }}>
          <Typography variant="subtitle1" textAlign="center">
            <strong>Container Type: </strong> {paymentContainerParams?.containerType?.label || "Not selected"}
          </Typography>
        </Box>
        <Box sx={{ minWidth: "200px", flex: "1 1 auto", textAlign: "center" }}>
          <Typography variant="subtitle1" textAlign="center">
            <strong>Vendor: </strong> {paymentContainerParams?.vendor?.label || "Not selected"}
          </Typography>
        </Box>
        <Box sx={{ minWidth: "200px", flex: "1 1 auto", textAlign: "center" }}>
          <Typography variant="subtitle1" textAlign="center">
            <strong>Invoice Status: </strong> {paymentContainerParams?.invoiceStatus || "Not selected"}
          </Typography>
        </Box>
        <Box sx={{ minWidth: "200px", flex: "1 1 auto", textAlign: "center" }}>
          <Typography variant="subtitle1" textAlign="center">
            <strong>Description: </strong> {paymentContainerParams?.description?.label || "Not selected"}
          </Typography>
        </Box>
      </Box>
    ),
    [paymentContainerParams]
  )

  // ===== RENDER =====

  return (
    <Stack p={2} spacing={3}>
      {/* Loading indicator */}
      <PageLoader isLoading={isPageLoading} />

      {/* Page title */}
      <Typography variant="h3" component="h2">
        Create Invoice
      </Typography>

      {/* Invoice form section */}
      <Grid container spacing={2}>
        {/* Client Name Field */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Controller
            name="clientName"
            control={control}
            rules={{ required: "Client Name is required" }}
            render={({ field, fieldState: { error } }) => (
              <SelectVendorField
                value={field.value ?? ""}
                onChange={(value: any) => field.onChange(value ?? "")}
                label="Vendor Name"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          {/* <Controller
            name="clientName"
            control={control}
            rules={{ required: "Client Name is required" }}
            render={({ field, fieldState: { error } }) => (
              <OutlinedInput {...field} label="Client Name" fullWidth error={!!error} helperText={error?.message} />
            )}
          /> */}
        </Grid>

        {/* Status Field */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Controller
            name="status"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <InputAutocomplete
                label="Status"
                value={invoiceStatusOptions.find(option => option.value === field.value)}
                onChange={(value: any) => field.onChange(value?.value)}
                options={invoiceStatusOptions}
                disableClearable
                placeholder="Select Status"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        {/* Tax Rate Field */}
        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Controller
            name="taxRate"
            control={control}
            rules={{ required: true, min: 0, max: 100 }}
            render={({ field, fieldState: { error } }) => (
              <OutlinedInput
                {...field}
                label="Tax Rate"
                type="number"
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>

        {/* Invoice Date Field */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="invoiceDate"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                label="Invoice Date"
                value={moment(field.value)}
                onChange={date => field.onChange(date?.toISOString())}
                error={!!error}
                helperText={error?.message}
                sx={{ width: "100%" }}
              />
            )}
          />
        </Grid>

        {/* Due Date Field */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Controller
            name="dueDate"
            control={control}
            rules={{ required: true }}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                label="Due Date"
                value={moment(field.value)}
                onChange={date => field.onChange(date?.toISOString())}
                error={!!error}
                helperText={error?.message}
                size="small"
              />
            )}
          />
        </Grid>
      </Grid>

      {/* Divider */}
      <Stack my={3}>
        <Divider />
      </Stack>

      {/* Sync Containers Section */}
      <SyncContainerButton onClick={toggleSyncModal}>
        <Typography variant="h4" textAlign="center">
          Sync Containers
        </Typography>
      </SyncContainerButton>

      {/* Payment Container Parameters Display */}
      {renderPaymentContainerParams}

      {/* Payment Containers Grid Section */}
      <Grid container spacing={2} mt={4}>
        {/* Available Payment Containers */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4" textAlign="center" mb={2}>
            Payment Containers
          </Typography>
          <DataGrid
            rows={paymentContainers}
            getRowId={row => row.id}
            columns={getPaymentContainerColumns({
              actionCell: availableContainersActionCell
            })}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnSorting
            loading={isPageLoading}
            paginationMode="server"
            rowCount={totalPaymentContainers}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            paginationModel={{
              page: paymentContainerPageNo,
              pageSize: paymentContainerPageSize
            }}
            onPaginationModelChange={e => handlePaginationChange(e.page, e.pageSize)}
            sx={DATA_GRID_STYLES}
          />
        </Grid>

        {/* Selected Payment Containers */}
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography variant="h4" textAlign="center" mb={2}>
            Selected Payment Containers
          </Typography>
          <DataGrid
            rows={selectedPaymentContainers}
            getRowId={row => row.id}
            columns={getPaymentContainerColumns({
              actionCell: selectedContainersActionCell
            })}
            disableRowSelectionOnClick
            disableColumnMenu
            disableColumnSorting
            rowCount={selectedPaymentContainers.length}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            sx={DATA_GRID_STYLES}
          />
        </Grid>
      </Grid>

      {/* Action Buttons */}
      <Stack mt={3} direction="row" spacing={2} justifyContent="flex-end">
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleSubmit(onSubmitInvoiceForm)}
          variant="contained"
          disabled={selectedPaymentContainers?.length === 0 || isPageLoading}
        >
          {isPageLoading ? "Creating..." : "Create Invoice"}
        </Button>
      </Stack>

      {/* Modals */}
      <SyncPaymentContainerModal
        open={isSyncPaymentContainerModalOpen}
        toggleDialog={toggleSyncModal}
        onSubmit={onSubmitPaymentContainerForm}
        paymentContainerParams={getValues("paymentContainerParams")}
      />

      <CreateInvoiceModal
        open={isCreateInvoiceModalOpen}
        onClose={toggleCreateInvoiceModal}
        invoiceData={invoiceData ?? ({} as CreateInvoiceForm)}
        selectedPaymentContainers={selectedPaymentContainers}
        afterSubmitForm={handleAfterInvoiceSubmit}
      />
    </Stack>
  )
}

export default CreateInvoice

// ===== STYLED COMPONENTS =====

/**
 * Styled button component for syncing containers
 * Provides visual feedback with hover and active states
 */
const SyncContainerButton = styled(Stack)({
  border: "2px solid #e0e0e0",
  borderRadius: "8px",
  padding: "16px",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: "#1976d2",
    backgroundColor: "#f5f5f5",
    transform: "translateY(-2px)",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)"
  },
  "&:active": {
    transform: "translateY(0)",
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
  }
})
